import { Check, Edit } from "@mui/icons-material";
import ContentCopy from "@mui/icons-material/ContentCopy";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
    Button,
    Input,
    LinearProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Snackbar,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { Box } from "@mui/system";
import { each } from "async";
import { clone } from "lodash";
import moment from "moment";
import { useState } from "react";
import * as commentUtilities from "../../stores/cohort/utils";
import MaterialAvatar from "../general/MaterialAvatar";
const MAX_COMMENT_CHARACTER_BEFORE_COMPRESS = 200;
const defaultBoxShadow = (theme) => `0 0 0 1px ${theme?.custom?.borderGrey}`;
const blueBoxShadow = "0 0 0 2px #1976D2";
const UNCOMPRESS_BUTTON_STYLE = {
    fontSize: "13px",
    display: "block",
    width: "100%",
    marginTop: "10px",
    cursor: "pointer",
};
const materialTextareaStyle =(theme)=>( {
    borderRadius: "4px",
    border: "none",
    boxShadow: defaultBoxShadow(theme),
    padding: "10px",
    resize: "none",
    width: "100%",
    transition: "box-shadow 0.2s ease",
    "::before, ::after": {
        border: "none",
    },
    ":hover": {
        boxShadow: blueBoxShadow,
        "::before, ::after": {
            border: "none !important",
        },
    },
    "&.Mui-focused": {
        boxShadow: blueBoxShadow,
        "::before, ::after": {
            border: "none !important",
        },
    },
});

const CanvasEntryComment = (props) => {
    const { org, team, cohort, entryID, user, comment, handleUpdateComment } =
        props;
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [editingCommentID, setEditingCommentID] = useState(null);
    const [editedCommentContent, setEditedCommentContent] = useState("");
    const [commentCollapseSet, setCommentCollapseSet] = useState({});
    const [showCopyNotification, setShowCopyNotification] = useState(false);
    const [isSavingComent, setIsSavingComent] = useState(false);
    const commentName = comment?.user?.name;
    const avatarSource = comment?.user?.photoURL;
    const origin = window.location.origin;
    const theme = useTheme();
    const baseCommentLink = commentUtilities.buildCommentLink(
        entryID,
        comment?.commentID
    );
    const commentLink = `${origin}${commentUtilities.buildForwardLink(
        baseCommentLink,
        org?.id,
        team?.id,
        cohort?.id
    )}`;

    const isCommentColapsed = (commentID) => {
        // If it's in the map then return it
        if (commentCollapseSet[commentID] !== undefined)
            return commentCollapseSet[commentID];

        // Assume it's compressed for now
        return true;
    };

    const setCommentCollapsed = (commentID, value) => {
        let newSet = clone(commentCollapseSet);

        newSet[commentID] = value;

        setCommentCollapseSet(newSet);
    };

    const renderCommentEditForm = () => (
        <Box sx={{ padding: "5px 0" }}>
            <Box sx={{ position: "relative" }}>
                <Input
                    sx={materialTextareaStyle(theme)}
                    value={editedCommentContent}
                    multiline={true}
                    maxRows={15}
                    maxLength={1000}
                    onChange={(e) => {
                        setEditedCommentContent(e.target.value);
                    }}
                />
                {isSavingComent && (
                    <LinearProgress
                        sx={{ position: "absolute", bottom: 0, left: 0, width: "100%" }}
                    />
                )}
            </Box>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    marginTop: "10px",
                }}
            >
                <Button
                    sx={{ marginRight: "5px" }}
                    variant="outlined"
                    size="small"
                    disabled={isSavingComent}
                    onClick={() => {
                        setEditedCommentContent("");
                        setEditingCommentID(null);
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    disabled={isSavingComent}
                    onClick={() => {
                        setIsSavingComent(true);

                        // Call for an update to this comment
                        handleUpdateComment({
                            commentID: comment?.commentID,
                            commentContent: editedCommentContent,
                            onSuccess: () => {
                                setEditedCommentContent("");
                                setEditingCommentID(null);
                                setIsSavingComent(false);
                            },
                            onFailure: () => {
                                setIsSavingComent(false);
                            },
                        });
                    }}
                >
                    Save
                </Button>
            </div>
            <div className="clearfix" />
        </Box>
    );

    const renderCommentContent = () => {
        // Check to see if there's no comment
        if (!comment?.content) {
            return null;
        }

        // Assume we don't need to compress
        let shouldWeCompress = false;
        let isOriginalLongEnough = false;
        let commentContent = clone(comment?.content);

        // If the size of the comment is above our threshold check the compression map
        if (commentContent.length > MAX_COMMENT_CHARACTER_BEFORE_COMPRESS) {
            isOriginalLongEnough = true;

            shouldWeCompress = isCommentColapsed(comment?.commentID);

            if (shouldWeCompress) {
                commentContent =
                    commentContent.substring(0, MAX_COMMENT_CHARACTER_BEFORE_COMPRESS) +
                    "...";
            }
        }

        // Now clean and prepare what's left
        let lineBits = [];
        let lines = commentContent.split("\n");

        each(lines, (line) => {
            lineBits.push(
                <span>
                    {line}
                    <br />
                </span>
            );
        });

        const uncompressButton = isOriginalLongEnough ? (
            <a
                style={UNCOMPRESS_BUTTON_STYLE}
                className={"commentActionLink"}
                onClick={() => {
                    setCommentCollapsed(comment?.commentID, !shouldWeCompress);
                }}
            >
                {shouldWeCompress ? "Show more" : "Show less"}
            </a>
        ) : null;

        return (
            <>
                {lineBits}
                {uncompressButton}
            </>
        );
    };

    const handleMenuButtonClick = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const renderCommentMenu = ({ commentLink }) => {
        return (
            <Menu
                open={!!menuAnchorEl}
                onClose={handleMenuClose}
                anchorEl={menuAnchorEl}
            >
                <Paper
                    sx={{
                        width: 150,
                    }}
                >
                    <MenuItem
                        sx={{
                            '&:hover': {
                                backgroundColor: theme?.custom?.hoverOptionColor,
                                cursor: 'pointer !important',
                                pointerEvents: 'all'
                            }
                        }}
                        onClick={() => {
                            navigator.clipboard.writeText(commentLink);
                            setShowCopyNotification(true);
                        }}
                    >
                        <ListItemIcon>
                            <ContentCopy fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Copy Link</ListItemText>
                    </MenuItem>
                    {comment?.userID === user?.userID && (
                        <MenuItem
                            sx={{
                                '&:hover': {
                                    backgroundColor: theme?.custom?.hoverOptionColor,
                                    cursor: 'pointer !important',
                                    pointerEvents: 'all'
                                }
                            }}
                            onClick={() => {
                                setEditedCommentContent(comment?.content);
                                setEditingCommentID(comment?.commentID);
                                setMenuAnchorEl(null);
                            }}
                        >
                            <ListItemIcon>
                                <Edit fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Edit</ListItemText>
                        </MenuItem>
                    )}
                </Paper>
            </Menu>
        );
    };

    const renderCommentHeader = ({
        name,
        avatarSource,
        date,
        avatarSize = 32,
        showMenu = false,
        commentLink = null,
    }) => {
        return (
            <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                sx={{ paddingBottom: "5px" }}
                spacing={2}
            >
                <Tooltip title={name}>
                    <MaterialAvatar
                        sx={{
                            width: avatarSize,
                            height: avatarSize,
                        }}
                        user={{
                            name,
                            photoURL: avatarSource,
                        }}
                    />
                </Tooltip>
                <Stack
                    direction="column"
                    flexGrow={1}
                >
                    <Typography>{name}</Typography>
                    <Typography
                        color="textSecondary"
                        variant="body2"
                    >
                        {moment(date).format("MMM DD 'YY")}
                    </Typography>
                </Stack>
                {showMenu && (
                    <Stack direction="column">
                        <MoreVertIcon
                            onClick={handleMenuButtonClick}
                            sx={{ cursor: "pointer" }}
                        />
                        {renderCommentMenu({ commentLink })}
                    </Stack>
                )}
            </Stack>
        );
    };

    const handleCopyNotificationClose = () => {
        setShowCopyNotification(false);
    };

    return (
        <>
            <Stack
                sx={{
                    padding: "10px",
                    borderBottom: "1px solid #dcdcdc",
                    borderRadius: "0px",
                }}
                spacing={0}
            >
                {renderCommentHeader({
                    name: commentName,
                    avatarSource: avatarSource,
                    date: comment?.created,
                    showMenu: true,
                    commentLink,
                })}
                <Stack>
                    {editingCommentID === comment?.commentID ? (
                        renderCommentEditForm()
                    ) : (
                        <Typography
                            color="textSecondary"
                            variant="body1"
                            sx={{ padding: "5px 0" }}
                        >
                            {renderCommentContent()}
                        </Typography>
                    )}
                </Stack>
            </Stack>
            <Snackbar
                open={showCopyNotification}
                autoHideDuration={3000}
                onClose={handleCopyNotificationClose}
                sx={{
                    color: "green",
                }}
                message={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Check
                            sx={{ color: "white", marginRight: "6px", fontSize: "16px" }}
                        />
                        The comment link has been coppied
                    </Box>
                }
            />
        </>
    );
};

export default CanvasEntryComment;
